// src/pages/SalonLogin.jsx - Updated with Staff UID login tab
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { loginSalonStaff, loginStaffByUID, resetPassword, logoutSalonUser } from '../api/salonAuthService';
import '../styles/auth.css';

async function isBookingModuleEnabled(salonId) {
  if (!salonId) return false;
  // Try exact salonId first, then with "-client" suffix (mirrors ModuleContext strategy)
  for (const docId of [salonId, `${salonId}-client`]) {
    const snap = await getDoc(doc(db, 'clients', docId));
    if (snap.exists()) {
      return snap.data()?.enabledModules?.bookings === true;
    }
  }
  return false;
}

function SalonLogin() {
  const [loginMode, setLoginMode] = useState('admin'); // 'admin' | 'staff'
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [staffEmail, setStaffEmail] = useState('');
  const [staffSalonId, setStaffSalonId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const navigate = useNavigate();
  const { salonId } = useParams();

  const { email, password } = formData;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await loginSalonStaff(email, password, salonId);
      navigate(`/BespokeBeauty/${salonId}/admin/home`);
    } catch (err) {
      if (err.message?.includes('Access denied')) {
        setError(err.message);
      } else if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        setError('Invalid email or password. Please try again.');
      } else if (err.code === 'auth/user-not-found') {
        setError('No account found with this email.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError('Login failed. Please check your credentials and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const resolvedSalonId = staffSalonId.trim() || salonId;
    if (!staffEmail.trim()) {
      setError('Please enter your email.');
      return;
    }
    if (!resolvedSalonId) {
      setError('Please enter your Salon ID.');
      return;
    }
    setLoading(true);
    try {
      // Authenticate first — the clients collection requires an active Auth session,
      // so the bookings-module check must happen AFTER sign-in.
      await loginStaffByUID(staffEmail.trim(), resolvedSalonId);

      const bookingsEnabled = await isBookingModuleEnabled(resolvedSalonId);
      if (!bookingsEnabled) {
        // Sign out the just-authenticated user before showing the modal
        await logoutSalonUser();
        setShowBookingModal(true);
        return;
      }

      navigate(`/BespokeBeauty/${resolvedSalonId}/staff/portal`);
    } catch (err) {
      setError(err.message || 'Login failed. Please check your email and Salon ID and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }
    try {
      await resetPassword(email);
      setResetSent(true);
    } catch (err) {
      setError('Error sending password reset email.');
    }
  };

  const switchMode = (mode) => {
    setLoginMode(mode);
    setError('');
    setResetSent(false);
    setFormData({ email: '', password: '' });
    setStaffEmail('');
    setStaffSalonId('');
    setShowBookingModal(false);
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="salon-branding">
          <h1 style={{
            color: 'var(--primary-coral)',
            marginBottom: '10px',
            fontFamily: "'Fraunces', serif",
            fontSize: '30px',
            fontWeight: 'bold'
          }}>
            BespokeBeauty
          </h1>
          {salonId && (
            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '20px', textTransform: 'capitalize' }}>
              {salonId.replace(/-/g, ' ')} - {loginMode === 'admin' ? 'Admin Portal' : 'Staff Portal'}
            </p>
          )}
        </div>

        {/* Login mode tabs */}
        <div style={{ display: 'flex', borderBottom: '2px solid #eee', marginBottom: '24px' }}>
          <button
            onClick={() => switchMode('admin')}
            style={{
              flex: 1,
              padding: '10px',
              background: 'none',
              border: 'none',
              borderBottom: loginMode === 'admin' ? '2px solid var(--primary-coral)' : '2px solid transparent',
              marginBottom: '-2px',
              color: loginMode === 'admin' ? 'var(--primary-coral)' : '#888',
              fontWeight: loginMode === 'admin' ? '600' : '400',
              cursor: 'pointer',
              fontFamily: 'Wix Madefor Text, sans-serif',
              fontSize: '0.9rem'
            }}
          >
            Admin / Manager Login
          </button>
          <button
            onClick={() => switchMode('staff')}
            style={{
              flex: 1,
              padding: '10px',
              background: 'none',
              border: 'none',
              borderBottom: loginMode === 'staff' ? '2px solid var(--primary-coral)' : '2px solid transparent',
              marginBottom: '-2px',
              color: loginMode === 'staff' ? 'var(--primary-coral)' : '#888',
              fontWeight: loginMode === 'staff' ? '600' : '400',
              cursor: 'pointer',
              fontFamily: 'Wix Madefor Text, sans-serif',
              fontSize: '0.9rem'
            }}
          >
            Staff Login
          </button>
        </div>

        {resetSent ? (
          <div className="reset-success">Password reset link sent to your email.</div>
        ) : (
          <>
            {error && <div className="auth-error">{error}</div>}

            {loginMode === 'admin' ? (
              <>
                <form onSubmit={handleAdminSubmit}>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className="form-group">
                    <label>Password</label>
                    <input
                      type="password"
                      name="password"
                      value={password}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    />
                  </div>
                  <button
                    type="submit"
                    className="auth-button"
                    disabled={loading}
                    style={{ backgroundColor: 'var(--primary-coral)' }}
                  >
                    {loading ? 'Logging in...' : 'Login'}
                  </button>
                </form>
                <button onClick={handlePasswordReset} className="forgot-password" disabled={loading}>
                  Forgot Password?
                </button>
              </>
            ) : (
              <>
                <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '16px', textAlign: 'center' }}>
                  Enter your email address and Salon ID to access your staff portal.
                </p>
                <form onSubmit={handleStaffSubmit}>
                  <div className="form-group">
                    <label>Salon ID</label>
                    <input
                      type="text"
                      value={staffSalonId || salonId || ''}
                      onChange={(e) => setStaffSalonId(e.target.value)}
                      placeholder="Enter your Salon ID"
                      required={!salonId}
                      disabled={loading}
                      style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={staffEmail}
                      onChange={(e) => setStaffEmail(e.target.value)}
                      placeholder="Enter your email address"
                      required
                      disabled={loading}
                    />
                  </div>
                  <button
                    type="submit"
                    className="auth-button"
                    disabled={loading}
                    style={{ backgroundColor: 'var(--primary-coral)' }}
                  >
                    {loading ? 'Logging in...' : 'Access Staff Portal'}
                  </button>
                </form>
              </>
            )}
          </>
        )}

        <div className="auth-footer" style={{ marginTop: '20px' }}>
          <p style={{ fontSize: '0.85rem', color: '#666' }}>
            Customer?{' '}
            <a
              href={`/BespokeBeauty/${salonId}/customer/login`}
              style={{ color: 'var(--primary-coral)', textDecoration: 'underline' }}
            >
              Customer Login
            </a>
          </p>
        </div>
      </div>

      {/* Booking Management module not enabled modal */}
      {showBookingModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: '#fff', borderRadius: '16px', padding: '36px 32px',
            maxWidth: '420px', width: '90%', textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.18)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔒</div>
            <h2 style={{
              fontFamily: "'Fraunces', serif", fontSize: '1.4rem',
              color: '#333', marginBottom: '12px'
            }}>
              Staff Portal Unavailable
            </h2>
            <p style={{ fontSize: '0.95rem', color: '#555', marginBottom: '8px', lineHeight: '1.6' }}>
              The Staff Portal is part of the <strong>Booking Management</strong> module, which is not currently enabled for this salon.
            </p>
            <p style={{ fontSize: '0.9rem', color: '#777', marginBottom: '24px', lineHeight: '1.6' }}>
              To activate this feature, please contact our sales team:
            </p>
            <a
              href="mailto:Sales@BespokeCRM.cloud"
              style={{
                display: 'inline-block', background: 'var(--primary-coral)', color: '#fff',
                padding: '10px 24px', borderRadius: '8px', textDecoration: 'none',
                fontWeight: '600', fontSize: '0.95rem', marginBottom: '16px'
              }}
            >
              Sales@BespokeCRM.cloud
            </a>
            <br />
            <button
              onClick={() => setShowBookingModal(false)}
              style={{
                background: 'none', border: 'none', color: '#aaa',
                fontSize: '0.85rem', cursor: 'pointer', marginTop: '4px'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SalonLogin;
